import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, ShieldCheck, Lock, PhoneCall } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, Words } from './motion/Reveal';

const IMG = 'https://images.unsplash.com/photo-1542330952-bffc55e812b2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';
const B = ({ children }) => <strong className="font-semibold text-[#0f172a]">{children}</strong>;

const COPY = {
  fr: {
    eyebrow: 'Préqualification rapide', start: 'Commencer ma préqualification', f1: ['Sans impact', 'sur votre cote de crédit'], f2: ['Quelques minutes', 'formulaire 100 % en ligne'],
    ctaTitle: 'Prêt à connaître votre budget?', ctaText: 'Faites votre préqualification maintenant. C’est gratuit, rapide et sans aucun engagement.',
    h1a: 'Préqualification de prêt auto', h1b: ' : rapide, facile et sans impact sur votre crédit',
    sub: <>Obtenez une <B>préqualification de prêt auto</B> en temps réel. Cette <B>démarche très simple</B> vous donne une <B>estimation de budget réaliste</B> pour votre futur véhicule, le tout <B>sans impact sur votre cote de crédit</B>.</>,
    chips: [[Lock, '100 % confidentiel'], [Clock, 'Quelques minutes'], [ShieldCheck, 'Sans impact sur le crédit']],
    advTitle: 'Les avantages d’une préqualification de prêt auto en ligne',
    advText: <>Une préqualification de prêt auto est une étape simple et sans stress. Elle vous donne une idée claire du budget que vous pouvez vous permettre. Vous gagnez du temps et vous magasinez en toute confiance, bien informé. C’est une démarche <B>sans aucun engagement</B> de votre part. <B>Si vous êtes prêt à aller de l’avant</B>, vous pouvez faire votre demande dès maintenant.</>,
    cta: 'Faire ma demande maintenant',
    howTitle: 'Comment fonctionne notre prêt auto en ligne rapide?',
    howIntro: 'Notre outil de préqualification est conçu pour être simple et transparent.',
    steps: [
      ['Complétez le formulaire', <>Le processus est entièrement en ligne et ne prend que quelques minutes.</>],
      ['Obtenez votre préqualification', <>Notre système effectue une <B>évaluation rapide sans impact sur votre crédit</B>. En quelques minutes, vous saurez si vous êtes admissible à un prêt auto.</>],
      ['Parlez à un expert', <>Un de nos conseillers vous contactera pour discuter de vos options et finaliser votre <B>prêt auto facilement</B>. Pour toute question, n’hésitez pas à <a href="tel:5144183968" className="text-[#1a5c3b] font-semibold underline underline-offset-4">contacter un expert</a>.</>],
    ],
    call: 'Parler à un expert : 514-418-3968',
  },
  en: {
    eyebrow: 'Quick pre-qualification', start: 'Start my pre-qualification', f1: ['No impact', 'on your credit score'], f2: ['A few minutes', '100% online form'],
    ctaTitle: 'Ready to know your budget?', ctaText: 'Get pre-qualified now. It is free, fast and with no commitment.',
    h1a: 'Car Loan Pre-Qualification', h1b: ': fast, easy and no impact on your credit',
    sub: <>Get a <B>car loan pre-qualification</B> in real time. This <B>very simple process</B> gives you a <B>realistic budget estimate</B> for your next vehicle, all <B>with no impact on your credit score</B>.</>,
    chips: [[Lock, '100% confidential'], [Clock, 'A few minutes'], [ShieldCheck, 'No credit impact']],
    advTitle: 'The benefits of an online car loan pre-qualification',
    advText: <>A car loan pre-qualification is a simple, stress-free step. It gives you a clear idea of the budget you can afford. You save time and shop with confidence, fully informed. There is <B>no commitment</B> on your part. <B>If you are ready to move forward</B>, you can apply right now.</>,
    cta: 'Apply now',
    howTitle: 'How does our fast online car loan work?',
    howIntro: 'Our pre-qualification tool is designed to be simple and transparent.',
    steps: [
      ['Fill out the form', <>The process is entirely online and only takes a few minutes.</>],
      ['Get your pre-qualification', <>Our system runs a <B>quick assessment with no impact on your credit</B>. Within minutes, you will know if you are eligible for a car loan.</>],
      ['Talk to an expert', <>One of our advisors will contact you to go over your options and finalize your <B>car loan easily</B>. Have a question? Feel free to <a href="tel:5144183968" className="text-[#1a5c3b] font-semibold underline underline-offset-4">contact an expert</a>.</>],
    ],
    call: 'Talk to an expert: 514-418-3968',
  },
};

const useCopy = () => {
  const { language } = useLanguage();
  return COPY[language === 'en' ? 'en' : 'fr'];
};

const HERO_IMG = 'https://images.unsplash.com/photo-1652509573480-a190f1c61f6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const FloatCard = ({ icon: Icon, title, text, className }) => (
  <div className={`absolute bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_rgba(15,23,42,0.15)] px-4 py-3 flex items-center gap-3 animate-[prequalFloat_6s_ease-in-out_infinite] ${className}`}>
    <div className="w-10 h-10 rounded-xl bg-[#1a5c3b] text-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
    <div>
      <p className="text-sm font-bold text-[#0f172a] leading-tight">{title}</p>
      <p className="text-xs text-slate-500">{text}</p>
    </div>
  </div>
);

const scrollToForm = () => document.querySelector('[data-testid="prequal-form-anchor"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export const PrequalHero = () => {
  const c = useCopy();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#eef6f0] via-white to-[#e6f4ea] pt-10 md:pt-16 pb-20 md:pb-28" data-testid="prequal-hero">
      <style>{'@keyframes prequalFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}'}</style>
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-[radial-gradient(#1a5c3b22_1px,transparent_1px)] [background-size:22px_22px]" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-emerald-200/40 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#1a5c3b] bg-white border border-emerald-200 shadow-sm px-4 py-1.5 rounded-full mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{c.eyebrow}
          </span>
          <Words as="h1" parts={[[c.h1a, 'text-transparent bg-clip-text bg-gradient-to-r from-[#1a5c3b] to-emerald-500'], [c.h1b]]} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6" />
          <Reveal delay={350} className="w-16 h-1 rounded-full bg-[#1a5c3b] mb-6" />
          <Reveal as="p" delay={450} className="text-lg md:text-xl font-light text-slate-500 leading-relaxed max-w-2xl" data-testid="prequal-hero-sub">{c.sub}</Reveal>
          <Reveal delay={600} className="flex flex-wrap items-center gap-4 mt-8">
            <button type="button" onClick={scrollToForm} className="group inline-flex items-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold text-base md:text-lg px-8 py-4 rounded-full shadow-[0_12px_35px_rgba(26,92,59,0.35)] hover:scale-[1.03] transition-[transform,background-color]" data-testid="prequal-start-btn">
              {c.start}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="tel:5144183968" className="inline-flex items-center gap-2 font-semibold text-[#1a5c3b] hover:underline underline-offset-4" data-testid="prequal-hero-call"><PhoneCall className="w-4 h-4" />514-418-3968</a>
          </Reveal>
          <div className="flex flex-wrap gap-2.5 mt-8">
            {c.chips.map(([Icon, label], i) => (
              <Reveal as="span" delay={750 + i * 100} key={label} className="inline-flex items-center gap-2 text-sm font-medium text-[#1a5c3b] bg-white/80 border border-emerald-100 px-4 py-2 rounded-full shadow-sm">
                <Icon className="w-4 h-4" />{label}
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={300} className="lg:col-span-5 relative hidden lg:block">
          <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-[#1a5c3b] to-emerald-400 rotate-3 opacity-90" />
          <img src={HERO_IMG} alt="" className="relative w-full h-[460px] object-cover rounded-[2rem] shadow-2xl" />
          <FloatCard icon={ShieldCheck} title={c.f1[0]} text={c.f1[1]} className="-left-12 top-10" />
          <FloatCard icon={Clock} title={c.f2[0]} text={c.f2[1]} className="-right-6 bottom-12 [animation-delay:1.5s]" />
        </Reveal>
      </div>
    </section>
  );
};

export const PrequalCta = () => {
  const c = useCopy();
  return (
    <section className="max-w-6xl mx-auto px-4 pb-20" data-testid="prequal-cta">
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] px-8 py-12 md:px-14 md:py-16 text-center">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px]" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-emerald-400/30 blur-3xl" />
        <div className="relative">
          <Words parts={[[c.ctaTitle]]} className="text-3xl md:text-4xl font-bold !text-white mb-4" />
          <Reveal as="p" delay={250} className="text-emerald-100 text-base md:text-lg max-w-2xl mx-auto mb-8">{c.ctaText}</Reveal>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 bg-white text-[#1a5c3b] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-lg" data-testid="prequal-cta-start">
              {c.start}<ArrowRight className="w-5 h-5" />
            </button>
            <a href="tel:5144183968" className="inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-colors" data-testid="prequal-cta-call">
              <PhoneCall className="w-5 h-5" />514-418-3968
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PrequalInfo = () => {
  const c = useCopy();
  return (
    <section className="max-w-6xl mx-auto px-4 py-16 md:py-24" data-testid="prequal-info">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <Reveal className="bg-[#f8faf7] rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.08)]" data-testid="prequal-benefits">
          <img src={IMG} alt="" className="w-full h-56 md:h-64 object-cover" loading="lazy" />
          <div className="p-7 md:p-10">
            <Words parts={[[c.advTitle]]} className="text-3xl md:text-4xl font-bold leading-tight mb-5" />
            <Reveal as="p" delay={150} className="text-slate-600 leading-relaxed mb-8">{c.advText}</Reveal>
            <Link to="/demander-un-pret-auto" className="group inline-flex items-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-[#1a5c3b]/25 hover:scale-[1.03] transition-[transform,background-color]" data-testid="prequal-apply-link">
              {c.cta}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
        <div className="lg:pt-6" data-testid="prequal-how">
          <Words parts={[[c.howTitle]]} className="text-3xl md:text-4xl font-bold leading-tight mb-3" />
          <Reveal as="p" delay={150} className="text-slate-500 text-lg mb-10">{c.howIntro}</Reveal>
          <div className="relative space-y-9">
            <div className="absolute left-8 md:left-9 top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-300 via-emerald-500 to-[#1a5c3b] opacity-40" />
            {c.steps.map(([title, text], i) => (
              <Reveal key={title} delay={200 + i * 180} className="relative flex gap-5 group" data-testid={`prequal-step-${i + 1}`}>
                <div className={`shrink-0 w-16 h-16 md:w-[72px] md:h-[72px] rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg ring-8 ring-white group-hover:scale-110 transition-transform ${['bg-emerald-400', 'bg-emerald-600', 'bg-[#1a5c3b]'][i]}`}>
                  {i + 1}
                </div>
                <div className="pt-1">
                  <h3 className="text-xl font-bold mb-1.5">{title} :</h3>
                  <p className="text-slate-600 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <a href="tel:5144183968" className="inline-flex items-center gap-2 mt-10 text-[#1a5c3b] font-semibold border-2 border-[#1a5c3b] hover:bg-[#1a5c3b] hover:text-white px-6 py-3 rounded-full transition-colors" data-testid="prequal-call-link">
            <PhoneCall className="w-4 h-4" />{c.call}
          </a>
        </div>
      </div>
    </section>
  );
};
